import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, ArrowRight, Check, ChevronRight, Package, Truck,
  Warehouse, ClipboardList, DollarSign, Receipt, Shield, Zap,
  BarChart3, Box, Globe, Clock, Star, Users, Lock, Cloud,
  Database, Settings, Layers, Target, Award, Bell, Calendar,
  Code, Heart, MessageSquare, HelpCircle, BookOpen, FileText,
  Briefcase, Search, Home, Play, Headphones, Mail, Phone, MapPin,
  Calculator, ChevronDown
} from 'lucide-react';
import { API_BASE } from '@/shared/services/api';

const API_URL = API_BASE;

// Icon mapping
const iconMap = {
  Package, Truck, BarChart3, DollarSign, Users, Shield, Zap, Star,
  Box, Warehouse, ClipboardList, Receipt, Calculator, Check, ArrowRight, Layers,
  Code, Database, Cloud, Lock, Settings, Heart, MessageSquare, Globe,
  HelpCircle, BookOpen, FileText, Briefcase, Target, Award,
  Clock, Calendar, Bell, Search, Home, Play, Headphones, Mail, Phone, MapPin
};
const getIcon = (n) => iconMap[n] || Package;

// ── Module Configuration ──
const MODULE_CONFIG = {
  wms: {
    name: 'Warehouse Management',
    subtitle: 'Full WMS Control',
    icon: 'Warehouse',
    color: { primary: 'indigo', accent: 'text-indigo-400', accentBg: 'bg-indigo-500', gradFrom: 'from-indigo-600', gradTo: 'to-indigo-900', pillBg: 'bg-indigo-500/10', pillText: 'text-indigo-400', borderAccent: 'border-indigo-500/30', ring: 'ring-indigo-500/20' },
    heroDescription: 'End-to-end warehouse visibility from receiving dock to shipping lane. Track every SKU, optimize picking routes, and build pallets with precision.',
    stats: [
      { value: '99.8%', label: 'Inventory Accuracy' },
      { value: '3x', label: 'Faster Picking' },
      { value: '< 2s', label: 'Barcode Scan Response' },
      { value: '24/7', label: 'Real-time Visibility' },
    ],
    features: [
      { title: 'Real-time Stock Tracking', desc: 'Monitor inventory levels across zones, bins, and pallets with live dashboards.' },
      { title: 'Pick/Pack Workflows', desc: 'Wave-based or order-based picking with mobile scanner integration.' },
      { title: 'Pallet Builder', desc: 'Automated pallet configuration with weight and dimension optimization.' },
      { title: 'Barcode Scanner Integration', desc: 'Native support for Zebra, Honeywell, and browser-based scanning.' },
      { title: 'Inventory Adjustments', desc: 'Cycle counts, write-offs, and inter-warehouse transfers with audit trails.' },
      { title: 'Zone & Bin Management', desc: 'Configurable warehouse topology with heat-map visualization.' },
    ],
    workflow: [
      { step: 1, title: 'Receive', desc: 'Goods arrive at the dock and are scanned into the system.' },
      { step: 2, title: 'Putaway', desc: 'System suggests optimal bin locations based on velocity and dimensions.' },
      { step: 3, title: 'Pick & Pack', desc: 'Operators follow optimized routes to fulfill orders efficiently.' },
      { step: 4, title: 'Ship', desc: 'Pallets are built, labeled, and dispatched to the carrier.' },
    ],
    faqs: [
      { q: 'Does WMS support multi-warehouse operations?', a: 'Yes. You can manage unlimited warehouses with cross-warehouse transfer workflows and consolidated reporting.' },
      { q: 'What barcode formats are supported?', a: 'EAN-13, UPC-A, Code 128, QR codes, and Data Matrix. Both 1D and 2D scanners are supported.' },
      { q: 'Can I integrate with existing ERP systems?', a: 'Absolutely. Our REST API and webhook system allow seamless integration with SAP, Oracle, and other ERP platforms.' },
    ]
  },
  logistics: {
    name: 'Logistics & Delivery',
    subtitle: 'EzParcel TMS Platform',
    icon: 'Truck',
    color: { primary: 'emerald', accent: 'text-emerald-400', accentBg: 'bg-emerald-500', gradFrom: 'from-emerald-600', gradTo: 'to-emerald-900', pillBg: 'bg-emerald-500/10', pillText: 'text-emerald-400', borderAccent: 'border-emerald-500/30', ring: 'ring-emerald-500/20' },
    heroDescription: 'Complete transportation management for your fleet. Optimize routes, track drivers in real-time, and deliver proof of every stop.',
    stats: [
      { value: '40%', label: 'Route Cost Reduction' },
      { value: '< 5s', label: 'GPS Update Interval' },
      { value: '98.5%', label: 'On-time Delivery Rate' },
      { value: '10K+', label: 'Deliveries Per Day' },
    ],
    features: [
      { title: 'Route Optimization', desc: 'AI-powered route planning considering traffic, time windows, and vehicle capacity.' },
      { title: 'Real-time GPS Tracking', desc: 'Live driver positions on an interactive map with ETA predictions.' },
      { title: 'Last Mile Delivery', desc: 'Configurable delivery zones, time-slot scheduling, and customer notifications.' },
      { title: 'Proof of Delivery (POD)', desc: 'Digital signature capture, photo evidence, and geotagged timestamps.' },
      { title: 'Fleet Management', desc: 'Vehicle maintenance schedules, fuel tracking, and compliance documentation.' },
      { title: 'Driver Mobile App', desc: 'Native PWA for drivers with turn-by-turn navigation and offline support.' },
    ],
    workflow: [
      { step: 1, title: 'Plan', desc: 'Routes are created and optimized based on delivery windows and capacity.' },
      { step: 2, title: 'Dispatch', desc: 'Drivers receive their routes and load manifests on their mobile app.' },
      { step: 3, title: 'Deliver', desc: 'Real-time tracking with customer notifications at each milestone.' },
      { step: 4, title: 'Confirm', desc: 'POD captured digitally — signature, photo, and GPS coordinates.' },
    ],
    faqs: [
      { q: 'How does route optimization work?', a: 'Our algorithm considers traffic patterns, delivery time windows, vehicle capacity, and driver schedules to minimize total distance and cost.' },
      { q: 'Can drivers work offline?', a: 'Yes. The PWA caches routes and POD data locally, syncing automatically when connectivity is restored.' },
      { q: 'What vehicle types are supported?', a: 'Any vehicle type — motorcycles, vans, trucks, and specialized refrigerated units. Each has configurable capacity and cost parameters.' },
    ]
  },
  procurement: {
    name: 'Procurement & Inbound',
    subtitle: 'Supplier & Purchasing Control',
    icon: 'ClipboardList',
    color: { primary: 'amber', accent: 'text-amber-400', accentBg: 'bg-amber-500', gradFrom: 'from-amber-600', gradTo: 'to-amber-900', pillBg: 'bg-amber-500/10', pillText: 'text-amber-400', borderAccent: 'border-amber-500/30', ring: 'ring-amber-500/20' },
    heroDescription: 'Streamline your entire purchasing cycle from requisition to delivery. Manage suppliers, compare quotes, and track every inbound shipment.',
    stats: [
      { value: '60%', label: 'Faster PO Processing' },
      { value: '25%', label: 'Cost Savings via RFQs' },
      { value: '100%', label: 'Audit Trail Coverage' },
      { value: '500+', label: 'Suppliers Managed' },
    ],
    features: [
      { title: 'Supplier Management', desc: 'Centralized vendor database with performance scoring and compliance tracking.' },
      { title: 'RFQ Workflows', desc: 'Multi-vendor quote comparison with automated scoring and approval routing.' },
      { title: 'Purchase Orders', desc: 'Configurable PO templates with multi-level approval workflows.' },
      { title: 'Goods Receipt & Inspection', desc: 'Quality checks at receiving with defect tracking and vendor claims.' },
      { title: 'Purchase Requisitions', desc: 'Department-level requisitions with budget validation and auto-routing.' },
      { title: 'Vendor Performance Tracking', desc: 'KPIs for delivery time, quality, and pricing consistency.' },
    ],
    workflow: [
      { step: 1, title: 'Requisition', desc: 'Departments submit material requests with specifications and urgency.' },
      { step: 2, title: 'Source', desc: 'RFQs are sent to qualified vendors for competitive bidding.' },
      { step: 3, title: 'Order', desc: 'Purchase orders are generated, approved, and sent to the selected supplier.' },
      { step: 4, title: 'Receive', desc: 'Goods are inspected, counted, and stored in designated warehouse zones.' },
    ],
    faqs: [
      { q: 'Can I set approval thresholds?', a: 'Yes. Multi-level approval chains can be configured based on PO value, department, or material category.' },
      { q: 'Does it support international vendors?', a: 'Yes, with multi-currency support, Incoterms configuration, and customs documentation generation.' },
      { q: 'How are vendor scores calculated?', a: 'Based on configurable KPIs including delivery punctuality, defect rate, price competitiveness, and responsiveness.' },
    ]
  },
  finance: {
    name: 'Financial Management',
    subtitle: 'Billing & Accounting Engine',
    icon: 'DollarSign',
    color: { primary: 'purple', accent: 'text-purple-400', accentBg: 'bg-purple-500', gradFrom: 'from-purple-600', gradTo: 'to-purple-900', pillBg: 'bg-purple-500/10', pillText: 'text-purple-400', borderAccent: 'border-purple-500/30', ring: 'ring-purple-500/20' },
    heroDescription: 'Full financial control for logistics operations. From freight billing to general ledger management, every transaction is tracked and auditable.',
    stats: [
      { value: '99.9%', label: 'Reconciliation Accuracy' },
      { value: '5x', label: 'Faster Invoice Processing' },
      { value: 'BRL/USD', label: 'Multi-currency Support' },
      { value: '0', label: 'Manual Data Entry' },
    ],
    features: [
      { title: 'Accounts Payable/Receivable', desc: 'Automated invoice matching, payment scheduling, and aging reports.' },
      { title: 'Freight Billing', desc: 'Complex freight rate tables with distance, weight, and zone-based calculations.' },
      { title: 'General Ledger', desc: 'Double-entry accounting with configurable chart of accounts.' },
      { title: 'Cost Center Accounting', desc: 'Allocate costs to vehicles, routes, warehouses, and departments.' },
      { title: 'Bank Reconciliation', desc: 'Automated matching of bank statements with internal transactions.' },
      { title: 'Financial Reports', desc: 'Balance sheets, P&L, cash flow, and custom KPI dashboards.' },
    ],
    workflow: [
      { step: 1, title: 'Record', desc: 'Transactions are captured from operations — shipments, receipts, orders.' },
      { step: 2, title: 'Process', desc: 'Invoices are generated, matched, and routed for approval.' },
      { step: 3, title: 'Reconcile', desc: 'Bank feeds are matched automatically with internal records.' },
      { step: 4, title: 'Report', desc: 'Financial statements and KPI dashboards are generated in real-time.' },
    ],
    faqs: [
      { q: 'Does it support Brazilian accounting standards?', a: 'Yes, fully compliant with CPC standards, SPED fiscal obligations, and IFRS convergence requirements.' },
      { q: 'Can I configure custom chart of accounts?', a: 'Absolutely. The chart of accounts is fully configurable with unlimited depth levels and cost center mappings.' },
      { q: 'Is multi-company consolidation supported?', a: 'Yes, with inter-company transaction elimination and consolidated financial reporting.' },
    ]
  },
  fiscal: {
    name: 'Fiscal Engine / OIG',
    subtitle: 'SEFAZ Compliance Platform',
    icon: 'Receipt',
    color: { primary: 'rose', accent: 'text-rose-400', accentBg: 'bg-rose-500', gradFrom: 'from-rose-600', gradTo: 'to-rose-900', pillBg: 'bg-rose-500/10', pillText: 'text-rose-400', borderAccent: 'border-rose-500/30', ring: 'ring-rose-500/20' },
    heroDescription: 'Critical compliance engine for Brazilian logistics. Emit NF-e, CT-e, and MDF-e documents with full SEFAZ integration and XML lifecycle management.',
    externalUrl: '/fiscal-emission-v2-presentation',
    stats: [
      { value: '100%', label: 'SEFAZ Compliance' },
      { value: '< 3s', label: 'Document Emission' },
      { value: '10yr', label: 'XML Archive Retention' },
      { value: '24/7', label: 'Automated Monitoring' },
    ],
    features: [
      { title: 'NF-e Emission', desc: 'Electronic invoice generation with automatic SEFAZ authorization and DANFE printing.' },
      { title: 'CT-e Emission', desc: 'Cargo transportation invoice with multi-modal support and tomador configuration.' },
      { title: 'SEFAZ Integration', desc: 'Direct web service communication with all Brazilian state tax authorities.' },
      { title: 'XML Lifecycle Management', desc: 'Emission, authorization, cancellation, correction letters, and inutilization.' },
      { title: 'Fiscal Document Archive', desc: 'Legally compliant 10-year archive with search, export, and audit capabilities.' },
      { title: 'DANFE/DACTE Generation', desc: 'PDF and thermal printer output for auxiliary fiscal documents.' },
    ],
    workflow: [
      { step: 1, title: 'Generate', desc: 'Fiscal documents are created from operational data — shipments and invoices.' },
      { step: 2, title: 'Authorize', desc: 'XML is digitally signed and transmitted to SEFAZ for authorization.' },
      { step: 3, title: 'Distribute', desc: 'DANFE/DACTE is generated and sent to the recipient with the authorized XML.' },
      { step: 4, title: 'Archive', desc: 'All documents are stored securely with full audit trail and retrieval capability.' },
    ],
    faqs: [
      { q: 'Which fiscal document types are supported?', a: 'NF-e (model 55), NFC-e (model 65), CT-e (model 57), MDF-e (model 58), and their correction/cancellation events.' },
      { q: 'What happens if SEFAZ is offline?', a: 'The system supports contingency modes (SCAN, DPEC, FS-DA) and automatically retransmits when services are restored.' },
      { q: 'Is digital certificate management included?', a: 'Yes. A1 and A3 certificate types are supported with expiration monitoring and automatic renewal alerts.' },
    ]
  }
};


export const ModuleDetailPage = () => {
  const { moduleSlug } = useParams();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  const mod = MODULE_CONFIG[moduleSlug];

  // If module not found, redirect
  useEffect(() => {
    if (!mod) navigate('/landing-preview#modules', { replace: true });
    // If this module has a dedicated external presentation, redirect there immediately
    else if (mod.externalUrl) {
      navigate(mod.externalUrl, { replace: true });
    }
  }, [mod, navigate]);

  if (!mod || mod.externalUrl) return null;

  const ModIcon = getIcon(mod.icon);
  const c = mod.color;

  return (
    <div className="min-h-screen bg-slate-900 text-white">

      {/* ── Hero Section ── */}
      <section className="min-h-screen flex flex-col justify-center px-4 relative overflow-hidden">
        {/* Gradient mesh background */}
        <div className={`absolute inset-0 bg-gradient-to-br ${c.gradFrom} ${c.gradTo} opacity-30`}></div>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-slate-900/80 to-slate-900"></div>

        <div className="relative z-10 max-w-6xl mx-auto w-full">
          {/* Back button */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate('/landing-preview#modules')}
            className="mb-8 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 backdrop-blur-sm border border-white/10 text-sm text-slate-300 hover:text-white hover:bg-white/10 transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Modules
          </motion.button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left — Text */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className={`w-14 h-14 rounded-2xl ${c.accentBg} flex items-center justify-center shadow-lg`}>
                  <ModIcon className="w-7 h-7 text-white" />
                </div>
                <div>
                  <span className={`text-xs font-bold uppercase tracking-widest ${c.accent}`}>{mod.subtitle}</span>
                </div>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6"
              >
                {mod.name}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-lg text-slate-400 font-light leading-relaxed max-w-xl mb-8"
              >
                {mod.heroDescription}
              </motion.p>

              <motion.a
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                href="/login"
                className={`inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-white bg-gradient-to-r ${c.gradFrom} ${c.accentBg} shadow-xl hover:scale-105 transition-all`}
              >
                Request a Demo
                <ArrowRight className="w-5 h-5" />
              </motion.a>
            </div>

            {/* Right — Stats Grid */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="grid grid-cols-2 gap-4"
            >
              {mod.stats.map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + i * 0.1 }}
                  className={`p-6 rounded-2xl bg-white/[0.04] backdrop-blur-sm border border-white/10 text-center`}
                >
                  <div className={`text-3xl sm:text-4xl font-black ${c.accent} mb-1`}>{stat.value}</div>
                  <div className="text-xs text-slate-500 uppercase tracking-widest font-semibold">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Features Section ── */}
      <section className="min-h-screen flex flex-col justify-center px-4 py-16">
        <div className="max-w-6xl mx-auto w-full">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-3">Core Capabilities</h2>
            <p className="text-slate-400 font-light max-w-2xl mx-auto">Everything you need to run a world-class {mod.name.toLowerCase()} operation.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mod.features.map((feat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className={`p-6 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-white/20 transition-all duration-300 hover:-translate-y-1`}
              >
                <div className={`w-10 h-10 rounded-xl ${c.pillBg} flex items-center justify-center mb-4`}>
                  <Check className={`w-5 h-5 ${c.pillText} stroke-[2.5]`} />
                </div>
                <h3 className="text-lg font-bold mb-2">{feat.title}</h3>
                <p className="text-sm text-slate-400 font-light leading-relaxed">{feat.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Workflow Section ── */}
      <section className="min-h-screen flex flex-col justify-center px-4 py-16 bg-slate-900/60">
        <div className="max-w-5xl mx-auto w-full">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-3">How It Works</h2>
            <p className="text-slate-400 font-light max-w-2xl mx-auto">A streamlined 4-step process from start to finish.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {mod.workflow.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="relative text-center"
              >
                {/* Step number */}
                <div className={`w-16 h-16 rounded-2xl ${c.accentBg} mx-auto flex items-center justify-center text-white text-2xl font-black mb-4 shadow-lg`}>
                  {step.step}
                </div>
                {/* Connector line */}
                {i < mod.workflow.length - 1 && (
                  <div className={`hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 ${c.accentBg} opacity-20`}></div>
                )}
                <h3 className="text-lg font-bold mb-2">{step.title}</h3>
                <p className="text-sm text-slate-400 font-light leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ Section ── */}
      <section className="min-h-screen flex flex-col justify-center px-4 py-16">
        <div className="max-w-3xl mx-auto w-full">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-3">Frequently Asked Questions</h2>
          </div>

          <div className="space-y-4">
            {mod.faqs.map((faq, i) => (
              <div key={i} className="rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden transition-all hover:border-white/20">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left"
                >
                  <span className="font-semibold text-white pr-4">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 flex-shrink-0 transition-transform duration-300 ${openFaq === i ? `rotate-180 ${c.accent}` : 'text-slate-500'}`} />
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <p className="px-6 pb-5 text-sm text-slate-400 leading-relaxed font-light">{faq.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-12">
            <a
              href="/login"
              className={`inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-white bg-gradient-to-r ${c.gradFrom} ${c.accentBg} shadow-xl hover:scale-105 transition-all`}
            >
              Get Started with {mod.name}
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default ModuleDetailPage;
